import { type Gender, type Locale, LOCALES, MIN_AGE, type MeDto } from '@spin/shared';
import { useState } from 'react';
import { game } from '../api/game';
import { http } from '../api/http';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { BottleArt } from '../table/Bottle';
import { tg } from '../tg/bridge';

export function Onboarding() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const [gender, setGender] = useState<Gender | null>(me.gender);
  const [name, setName] = useState(me.displayName);
  const [birthYear, setBirthYear] = useState('');
  const [adult, setAdult] = useState(false);
  const [busy, setBusy] = useState(false);
  const year = new Date().getFullYear();
  const yearNum = birthYear ? Number(birthYear) : null;
  const underage = yearNum !== null && year - yearNum < MIN_AGE;

  const setLocale = async (locale: Locale) => {
    useSession.getState().setMe({ ...me, locale });
    await http.patch<MeDto>('/me', { locale }).catch(reportError);
  };

  const submit = async () => {
    if (!gender || !adult || underage) return;
    setBusy(true);
    try {
      const updated = await http.post<MeDto>('/me/onboarding', {
        gender,
        adultConfirmed: true,
        displayName: name.trim() || undefined,
        birthYear: yearNum,
        locale: me.locale,
      });
      useSession.getState().setMe(updated);
      tg.haptic('success');
      game.connect();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="game">
      <div className="board" aria-hidden />
      <div className="dialog-layer centered onboarding-layer">
        <div className="dialog onboarding" role="dialog" data-testid="onboarding">
          <div className="dialog-body">
            <div className="onboarding-logo" aria-hidden>
              <BottleArt className="onboarding-bottle" />
              <span>💋</span>
            </div>
            <h1>{t('onboarding.title')}</h1>
            <p className="muted">{t('onboarding.subtitle')}</p>
            <div className="onboarding-langs">
              {LOCALES.map((l) => (
                <button key={l} className={me.locale === l ? 'on' : ''} onClick={() => void setLocale(l)}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <label className="onboarding-field">
              {t('onboarding.name')}
              <input
                className="input"
                value={name}
                maxLength={24}
                onChange={(e) => setName(e.target.value)}
                data-testid="onboarding-name"
              />
            </label>
            <div className="onboarding-field">
              {t('onboarding.gender')}
              <div className="gender-tiles">
                {(['FEMALE', 'MALE'] as const).map((g) => (
                  <button
                    key={g}
                    className={gender === g ? `on ${g.toLowerCase()}` : g.toLowerCase()}
                    onClick={() => setGender(g)}
                    aria-pressed={gender === g}
                    data-testid={`onboarding-${g}`}
                  >
                    <span aria-hidden>{g === 'FEMALE' ? '👩' : '👨'}</span>
                    {t(`gender.${g}`)}
                  </button>
                ))}
              </div>
            </div>
            <label className="onboarding-field">
              {t('onboarding.birth_year')} ({t('common.optional')})
              <input
                className="input"
                inputMode="numeric"
                placeholder={String(year - 25)}
                value={birthYear}
                maxLength={4}
                onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, ''))}
                data-testid="onboarding-year"
              />
              <small className="muted">{t('onboarding.birth_year_hint')}</small>
              {underage && <small className="error-text">{t('onboarding.underage')}</small>}
            </label>
            <div className="onboarding-rules">
              <b>{t('onboarding.rules_title')}</b>
              <ul>
                <li>{t('onboarding.rule_respect')}</li>
                <li>{t('onboarding.rule_virtual')}</li>
                <li>{t('onboarding.rule_block')}</li>
              </ul>
              <label>
                <input
                  type="checkbox"
                  checked={adult}
                  onChange={(e) => setAdult(e.target.checked)}
                  data-testid="onboarding-adult"
                />
                <span>{t('onboarding.adult')}</span>
              </label>
            </div>
            <button
              className="btn btn-green btn-block onboarding-start"
              disabled={busy || !gender || !adult || underage}
              onClick={() => void submit()}
              data-testid="onboarding-start"
            >
              {t('onboarding.start')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
